import React from 'react';
import { useTranslation } from 'react-i18next';
import { PersonProfileImageSmall, PersonProfileImageSmallInfoCard } from '../PersonProfileImageSmall';
import { Hyperlink } from '../../atoms/Hyperlink';
import { Namespace } from '../../../translation/namespace';
import { TextMarker } from '../../../_lib/components/TextMarker/TextMarker';
import Bem from '../../../lib/bemUtils/bem';
import { ProfileImageSmall } from '../../../types/api/ImageType';
import IUrl, { ISonURL } from '../../../types/IUrl';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { PersonInformationModifier } from './PersonInformation.modifier';

export type PersonInformationProps = {
  profileName?: string;
  profileImageUrl?: ProfileImageSmall;
  infoCardUrl?: IUrl;
  profileLink?: ISonURL;
  phoneLink?: ISonURL;
  emailLink?: ISonURL;
  roleNames?: string[];
  deletedUser?: boolean;
  orgUnit?: string;
  modifier?: Modifier<
    PersonInformationModifier.POST | PersonInformationModifier.THREADLIST | PersonInformationModifier.TICKET
  >;
};
/**
 * PersonInforamation with profile image and list of definitions
 */
export function PersonInformation({
  deletedUser,
  emailLink,
  infoCardUrl,
  modifier,
  orgUnit,
  phoneLink,
  profileImageUrl,
  profileLink,
  profileName,
  roleNames,
}: PersonInformationProps) {
  const { t } = useTranslation(Namespace.PersonInformation);

  /**
   * Creates an Array of definitions by using properties
   * @returns {Array}
   */
  const createDefinitions = () => {
    const definitions = [];

    // Definitions
    /**
     * This will be returned if user is completely deleted.
     * We expect default image in json from backend
     * */
    if (deletedUser && !profileName) {
      definitions.push(
        <p key="deletedUser">
          <dfn className="invisible">{t('name')}: </dfn>
          <strong className="person-information__name">{t('deletedUser')}</strong>
        </p>,
      );
    }

    if (profileLink) {
      definitions.push(
        <p key={profileLink.url + profileLink.name}>
          <dfn className="invisible">{t('name')}: </dfn>
          <Hyperlink to={profileLink.url} className="person-information__link">
            {modifier === PersonInformationModifier.TICKET
              ? (
                  profileLink.name
                )
              : (
                  <strong className="person-information__name">{profileLink.name}</strong>
                )}
          </Hyperlink>
        </p>,
      );
      /**
       * This will be returned if user is partially deleted.
       * We expect default image in json from backend
       * */
    } else if (profileName) {
      definitions.push(
        <p key={profileName}>
          <dfn className="invisible">{t('name')}: </dfn>
          <strong className="person-information__name">
            {profileName} {` (${t('deleted')})`}
          </strong>
        </p>,
      );
    }

    // return roles
    if (roleNames && roleNames.length) {
      const dfn = roleNames.length > 1 ? t('role_plural') : t('role');
      definitions.push(
        <p key={roleNames + (profileName || 'unknown')}>
          <dfn className="invisible">{dfn}: </dfn>
          {roleNames.join(', ')}
        </p>,
      );
    }

    // return phone number
    if (phoneLink && phoneLink.url) {
      definitions.push(
        <p key={phoneLink.url + profileName}>
          <dfn className="invisible">{t('phoneNumber')}: </dfn>
          <Hyperlink
            routing={false}
            to={phoneLink.url}
            className="person-information__link"
            title={t('phoneNumberTitle')}
          >
            {phoneLink.name}
          </Hyperlink>
        </p>,
      );
    }

    // return email
    if (emailLink) {
      definitions.push(
        <p key={emailLink.url}>
          <dfn className="invisible">{t('email')}: </dfn>
          {emailLink.url
            ? (
                <Hyperlink routing={false} to={emailLink.url} className="person-information__link">
                  <TextMarker text={emailLink.name} type="strong" />
                </Hyperlink>
              )
            : (
                <span>
                  <TextMarker text={emailLink.name} type="strong" />
                </span>
              )}
        </p>,
      );
    }

    if (orgUnit && orgUnit !== '') {
      definitions.push(
        <p key={`${orgUnit}-${profileName}`}>
          <dfn className="invisible">{t('orgunit')}</dfn>
          {orgUnit}
        </p>,
      );
    }

    return definitions;
  };

  return (
    <div className={Bem('person-information', modifier)}>
      {profileImageUrl && (
        <div className="person-information__image-container">
          {infoCardUrl
            ? (
                <PersonProfileImageSmallInfoCard
                  personInfoCardUrl={infoCardUrl}
                  profileImageUrl={profileImageUrl}
                  profileName={profileName}
                />
              )
            : (
                <PersonProfileImageSmall profileImageUrl={profileImageUrl} profileName={profileName} />
              )}
        </div>
      )}
      <div className="person-information__list">{createDefinitions()}</div>
    </div>
  );
}
